// Wat er moet gebeuren als je adressen aan een wervingsactie toevoegt of eruit
// haalt: welke rijen aanmaken, welke terugzetten en welke zacht verwijderen.
//
// Nodig omdat Detail.jsx dit per adres inline deed, en daarbij alleen naar de
// levende rijen keek. Vink je een adres uit en later weer aan, dan kwam er een
// tweede document naast het verwijderde, en het veldwerk van het eerste bleef
// onvindbaar liggen. Bij het kiezen van een hele straat of wijk wordt dat een
// veelvoud. Hier is het één regel voor één adres én voor 3.550.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)
// AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans

import notDeleted from '../notDeleted'
import { EMPTY_REGISTRATIE } from './registratieOptions'

/**
 * @typedef {object} Registratie
 * @property {string} docid - Firestore-documentid
 * @property {string} Key - Adressleutel (BAGid, of postcode-huisnummer-toevoeging)
 * @property {boolean|string} [Deleted] - Zacht verwijderd (ADR-008)
 * @property {{ toMillis: () => number }} [DeletedAt] - Moment van verwijderen
 */

/**
 * @typedef {object} SelectiePlan
 * @property {Array<object>} toCreate - Adressen waarvoor een nieuwe rij nodig is
 * @property {Array<Registratie>} toReactivate - Zacht verwijderde rijen die terug moeten
 * @property {number} alreadySelected - Adressen die al een levende rij hebben
 */

/**
 * @typedef {object} LoslaatPlan
 * @property {Array<Registratie>} toSoftDelete - Levende rijen die zacht verwijderd worden
 * @property {Array<Registratie>} withFieldwork - Het deel daarvan waar al iets in staat
 */

// De velden van het registratieformulier, plus het huishouden dat eruit kan
// zijn aangemaakt. Staat hier iets in, dan is er aan de deur al werk gedaan en
// mag loslaten niet ongevraagd.
const FIELDWORK_FIELDS = [...Object.keys(EMPTY_REGISTRATIE), 'HouseholdAdresID']

/**
 * @param {*} value
 * @returns {boolean}
 */
const isFilled = (value) => {
    if (typeof value === 'string') return value.trim() !== ''
    if (Array.isArray(value)) return value.length > 0
    return value === true
}

/**
 * Of er in een rij al veldwerk staat.
 *
 * @param {object} registratie
 * @returns {boolean}
 */
const hasFieldwork = (registratie) => FIELDWORK_FIELDS.some((field) => isFilled(registratie?.[field]))

/**
 * @param {Registratie} r
 * @returns {number}
 */
const deletedAtMillis = (r) => (typeof r?.DeletedAt?.toMillis === 'function' ? r.DeletedAt.toMillis() : 0)

/**
 * Welke verwijderde rij terug moet als er meer dan één is: eerst die met
 * veldwerk, dan de laatst verwijderde, dan op docid zodat de keuze niet van de
 * volgorde van Firestore afhangt.
 *
 * @param {Registratie} a
 * @param {Registratie} b
 * @returns {number}
 */
const compareRevivable = (a, b) =>
    Number(hasFieldwork(b)) - Number(hasFieldwork(a)) ||
    deletedAtMillis(b) - deletedAtMillis(a) ||
    String(a.docid).localeCompare(String(b.docid))

/**
 * @param {Array<Registratie>} registraties
 * @returns {Map<string, { alive: Array<Registratie>, deleted: Array<Registratie> }>}
 */
const indexByKey = (registraties) => {
    const byKey = new Map()
    for (const r of registraties || []) {
        if (!r?.Key) continue
        if (!byKey.has(r.Key)) byKey.set(r.Key, { alive: [], deleted: [] })
        byKey.get(r.Key)[notDeleted(r) ? 'alive' : 'deleted'].push(r)
    }
    return byKey
}

/**
 * @param {Array<{ Key: string }>} addresses
 * @returns {Array<{ Key: string }>} Op `Key` ontdubbeld, in de oorspronkelijke volgorde
 */
const uniqueByKey = (addresses) => {
    const seen = new Set()
    return (addresses || []).filter((a) => {
        if (!a?.Key || seen.has(a.Key)) return false
        seen.add(a.Key)
        return true
    })
}

/**
 * Wat er moet gebeuren om deze adressen aan de actie toe te voegen.
 *
 * @param {Array<{ Key: string }>} addresses - Adressen die gekozen worden
 * @param {Array<Registratie>} registraties - Alle rijen van de actie, óók de verwijderde
 * @returns {SelectiePlan}
 */
const planSelect = (addresses, registraties) => {
    const byKey = indexByKey(registraties)
    const plan = { toCreate: [], toReactivate: [], alreadySelected: 0 }

    for (const a of uniqueByKey(addresses)) {
        const existing = byKey.get(a.Key)
        if (existing?.alive.length) plan.alreadySelected += 1
        else if (existing?.deleted.length) plan.toReactivate.push([...existing.deleted].sort(compareRevivable)[0])
        else plan.toCreate.push(a)
    }

    return plan
}

/**
 * Wat er moet gebeuren om deze adressen uit de actie te halen.
 *
 * @param {Array<{ Key: string }>} addresses - Adressen die losgelaten worden
 * @param {Array<Registratie>} registraties - Alle rijen van de actie
 * @returns {LoslaatPlan}
 */
const planDeselect = (addresses, registraties) => {
    const byKey = indexByKey(registraties)
    const toSoftDelete = uniqueByKey(addresses).flatMap((a) => byKey.get(a.Key)?.alive || [])

    return { toSoftDelete, withFieldwork: toSoftDelete.filter(hasFieldwork) }
}

export { hasFieldwork, planSelect, planDeselect }
